import React from "react";
import { uniqByShape } from "../../../util/helpers";
import { featureEnabled } from "../../../util/feature";

const normalizePolicies = (policies) => {
  return uniqByShape(policies.map((policy) => ({ name: policy[0], url: policy[1] })));
};

const buyAndBillMessage = (value) => {
  if (value === true) return "True";
  if (value === false) return "False";
  return "Unknown";
};

const cptCodesMultiplier = (data) => {
  const codeCounts = {};
  data.forEach((item) => {
    codeCounts[item.code] = (codeCounts[item.code] || 0) + 1;
  });

  const cptMultipliers = [];
  const seenCodes = new Set();

  data.forEach((item) => {
    if (!seenCodes.has(item.code)) {
      seenCodes.add(item.code);
      if (codeCounts[item.code] > 1) {
        item.code = `${item.code}x${codeCounts[item.code]}`;
      }
      cptMultipliers.push(item);
    }
  });

  return cptMultipliers;
};

const policyRows = ({ policyInfo, biState, verifiedCptCodes }) => {
  const cptCodes = cptCodesMultiplier(verifiedCptCodes);
  if (biState === "error") {
    return cptCodes?.map((cptCode) => {
      return {
        code: cptCode.code,
        pa_required: cptCode.lab_specific_pa_required || cptCode.cpt_code_pa_required,
        group_id_pa_not_required: cptCode.group_id_pa_not_required,
        pa_approval_percentage: cptCode.pa_approval_percentage || null,
      };
    });
  } else {
    if (policyInfo) {
      const cptCoverages = {};

      cptCodes.forEach((cptCode) => {
        const baseCode = cptCode.code.split("x")[0];
        if (policyInfo?.cpt_coverage && policyInfo?.cpt_coverage[baseCode]) {
          cptCoverages[cptCode.code] = policyInfo?.cpt_coverage[baseCode];
        }
      });

      return Object.keys(cptCoverages || {}).map((cptCode) => {
        const element = cptCoverages[cptCode];
        const verifiedCptCode = verifiedCptCodes.find((cpt) => cpt.code === cptCode);

        return {
          code: cptCode,
          pa_require_status: element.pa_require_status,
          covered: element?.covered,
          policies: normalizePolicies(element?.policies),
          pa_required: element?.pa_required,
          gc_requirement: element?.gc_requirement || false,
          group_id_pa_not_required: cptCode.group_id_pa_not_required || verifiedCptCode?.group_id_pa_not_required,
          pa_approval_percentage: cptCode.pa_approval_percentage || verifiedCptCode?.pa_approval_percentage || null,
          predictive_coverage_analysis: element?.predictive_coverage_analysis,
          buy_and_bill: element?.buy_and_bill,
        };
      });
    }
  }

  return [{ code: null, policies: null, paRequired: null }];
};

const PolicyRow = ({ key, cptCoverage, showPredictiveCoverage, showBuyAndBill, biNoOonBenefitsError }) => {
  return (
    <tr key={key} className="js-table-row">
      <td className="cell-data" id={`${cptCoverage.code}-code`}>
        <h5 className="fw-bold text-start text-dark">{cptCoverage.code}</h5>
      </td>
      <td className="cell-data" id={`${cptCoverage.code}-pa-required`}>
        <h5 className="fw-bold text-start text-dark">{cptCoverage.pa_require_status}</h5>
      </td>
      <td className="cell-data" id={`${cptCoverage.code}-covered-under-medical-policy`}>
        <h5 className="fw-normal text-start text-dark">{paRequiredMessage(cptCoverage.covered, biNoOonBenefitsError)}</h5>
      </td>
      {showBuyAndBill && (
        <td className="cell-data" id={`${cptCoverage.code}-buy-and-bill`}>
          <h5 className="fw-normal text-start text-dark">{buyAndBillMessage(cptCoverage.buy_and_bill)}</h5>
        </td>
      )}
      {showPredictiveCoverage && (
        <>
          <td className="cell-data" id={`${cptCoverage.code}-predictive-coverage`}>
            <h5 className="fw-normal text-start text-dark">
              {cptCoverage.pa_approval_percentage === null ? <strong>Unavailable</strong> : predictiveCoverageMessage(cptCoverage)}
            </h5>
          </td>
        </>
      )}
    </tr>
  );
};

const paRequiredMessage = (covered, biNoOonBenefitsError) => {
  if (covered == true) {
    return "Covered";
  } else if (covered == false || biNoOonBenefitsError) {
    return "Not Covered";
  } else {
    return "Unknown";
  }
};

const predictiveCoverageMessage = (cptCoverage) => {
  return (
    <>
      <strong>{cptCoverage.pa_approval_percentage}%</strong> likelihood of PA Approval
    </>
  );
};

export const PolicyInfo = ({
  policyInfo,
  paRequirementsConfigured,
  biState,
  biNoOonBenefitsError,
  verifiedCptCodes,
  trizettoEnabled,
  showPredictiveCoverage,
  isPharmacyOwnedByClinic,
}) => {
  const showBuyAndBill = featureEnabled("buy_and_bill");
  const coverage = policyRows({
    policyInfo,
    biState,
    verifiedCptCodes,
  });

  const predictiveTooltipMessage = `careviso’s analytics model
predicts the likelihood of health plan coverage 
or prior authorization approval, built off 
a complex machine learning platform.`;

  return (
    <table id="policy-section" className="table table-striped mt-2 rounded">
      <thead>
        <tr>
          <th title="CPT Codes.">{isPharmacyOwnedByClinic ? "Procedure Code" : "CPT Code"}</th>
          <th title="Requires Prior Authorization?">PA Required</th>
          <th title="Covered Under Medical Policy">Covered Under Medical Policy</th>
          {showBuyAndBill && <th title="Buy and Bill Allowed">Buy and Bill Allowed</th>}
          {showPredictiveCoverage && <th title={predictiveTooltipMessage}>Predictive Coverage Analysis</th>}
        </tr>
      </thead>

      <tbody>
        {policyInfo
          ? coverage.map((cptCoverage, cptCoverageIndex, _code) => (
              <PolicyRow
                key={cptCoverageIndex}
                cptCoverage={cptCoverage}
                showPredictiveCoverage={showPredictiveCoverage}
                showBuyAndBill={showBuyAndBill}
                biNoOonBenefitsError={biNoOonBenefitsError}
              />
            ))
          : coverage.map((cptCoverage, index) => (
              <tr key={index} className="js-table-row">
                <td className="cell-data">
                  <h5 className="fw-bold text-start text-dark">{cptCoverage.code}</h5>
                </td>
                <td className="cell-data">
                  <h5 className="fw-bold text-start text-dark">{cptCoverage.pa_require_status}</h5>
                </td>
                <td className="cell-data">
                  <h5 className="fw-normal text-start text-dark">{paRequiredMessage(cptCoverage.covered, biNoOonBenefitsError)}</h5>
                </td>
                {showBuyAndBill && (
                  <td className="cell-data">
                    <h5 className="fw-normal text-start text-dark">{buyAndBillMessage(cptCoverage.buy_and_bill)}</h5>
                  </td>
                )}
                {showPredictiveCoverage && (
                  <td className="cell-data">
                    <h5 className="fw-normal text-start text-dark">
                      {cptCoverage.pa_approval_percentage === null ? <strong>Unavailable</strong> : predictiveCoverageMessage(cptCoverage)}
                    </h5>
                  </td>
                )}
              </tr>
            ))}
      </tbody>
    </table>
  );
};
